/**
 * The UI-language request header, in one place.
 * --------------------------------------------------------------------------
 * TRUS-446 resolves tenant-authored labels — custom field names, form and
 * section titles, picklist option labels — on the SERVER, so that the web app
 * and the Flutter app get Spanish from a single code path. The server needs to
 * be told which language to resolve into, and `X-WeaveIn-Language` is how a
 * client says so.
 *
 * WHY THIS IS ITS OWN MODULE
 * The header was originally set inside `services/apiClient.ts`'s request
 * interceptor, which covers only requests made through `apiClient`. But about
 * thirty service modules build their OWN `axios.create()` instance with a
 * hand-rolled interceptor that sets `Authorization` and `X-CBO-ID` and nothing
 * else — including the two that call the exact endpoints TRUS-446 hooked:
 * `services/api/forms.ts` (form templates) and `services/api/members.ts`
 * (member profile fields). Those requests arrived with no language header, the
 * server fell back to `user_preferences.language_code` (which the in-app
 * language switcher does not write), resolved English, and returned an empty
 * overlay. The result was Spanish chrome around English field labels — reported
 * at Somos Mayfair, whose Spanish content was fully authored and correct.
 *
 * So the rule is: any axios instance that talks to the WeaveIn API applies this.
 *
 * It reads `localStorage` rather than importing the i18n instance on purpose —
 * that keeps it free of a module cycle with the app bootstrap, and it reads the
 * SAME key i18next renders chrome from, so a screen can never show Spanish
 * buttons around English labels.
 */

/** i18next's own persistence key — see `src/i18n/config.ts` (`lookupLocalStorage`). */
const UI_LANGUAGE_STORAGE_KEY = 'i18nextLng';

export const UI_LANGUAGE_HEADER = 'X-WeaveIn-Language';

/** The language the UI is currently rendering in, or null if nothing is stored. */
export function currentUiLanguage(): string | null {
  try {
    return localStorage.getItem(UI_LANGUAGE_STORAGE_KEY);
  } catch {
    // Private-mode / storage-disabled browsers throw on access. A missing
    // header means the server falls back to the saved preference, which is a
    // worse answer than the live one but never an error.
    return null;
  }
}

/**
 * Stamp the UI language onto an outgoing request.
 *
 * Takes and returns the axios request config so it can be dropped into an
 * existing interceptor body. Never overwrites a header the caller set
 * explicitly, so a deliberate per-request override still wins.
 */
export function applyUiLanguageHeader<T extends { headers: any }>(config: T): T {
  const uiLanguage = currentUiLanguage();
  if (uiLanguage && config.headers && !config.headers[UI_LANGUAGE_HEADER]) {
    config.headers[UI_LANGUAGE_HEADER] = uiLanguage;
  }
  return config;
}
